<template>
	<!-- Below 1024px an order takes the outlet in place of the list (Figma "Order details" mobile: the breadcrumb, no
	     page title); from 1024px it opens in the drawer over the list, which stays where it was. -->
	<OrderDetails
		v-if="orderId && !isWide"
		:order-id="orderId"
		variant="page"
		:heading-id="DETAILS_HEADING_ID"
		@close="closeDetails"
	/>

	<PortalPage v-else :title="section.label" subtitle="Manage your orders and refills">
		<OrdersListSkeleton v-if="!model && store.listState !== 'failed'" />

		<!-- The list's read failed with nothing to show. The renewal card stays under the error when the feed answered:
		     a failed list never hides an outstanding action. -->
		<LayoutFlex v-else-if="!model" class="orders" padding="0" gap="var(--orders-gap)">
			<OrdersStateCard
				ref="errorCard"
				title="Something went wrong"
				message="Make sure you’re connected to the internet and try again"
				action-label="Try again"
				@action="retryLoad"
			/>
			<RenewalActionBanner
				v-if="renewal"
				:banner="renewal"
				:actionable="renewalDestination !== null"
				@action="openRenewal"
			/>
		</LayoutFlex>

		<LayoutFlex v-else class="orders" padding="0" gap="var(--orders-gap)">
			<!-- The list's re-read or the feed's failed behind what is shown (the card included): one notice, one retry
			     that reads both again. -->
			<OrdersStateCard
				v-if="store.stale"
				ref="staleNotice"
				message="We couldn’t refresh your orders. What you see may be out of date."
				action-label="Try again"
				:busy="retryingRefresh"
				@action="retryRefresh"
			/>

			<!-- Without its header the group itself takes the focus after a list retry (refocusAfterRetry). -->
			<LayoutFlex
				ref="firstGroup"
				class="orders__group"
				padding="0"
				gap="var(--size-8)"
				:tabindex="model.showInProgressHeader ? undefined : -1"
			>
				<OrdersGroupHeader
					v-if="model.showInProgressHeader"
					ref="inProgressHeader"
					class="orders__group-header"
					title="In progress"
					:count="model.inProgressCount"
				/>
				<RenewalActionBanner
					v-if="renewal"
					:banner="renewal"
					:actionable="renewalDestination !== null"
					@action="openRenewal"
				/>
				<LayoutFlex
					v-if="model.isEmpty"
					class="orders__empty"
					padding="var(--size-20) var(--orders-empty-inline)"
					gap="var(--size-8)"
					background="--background-white"
					corner-radius="var(--size-24)"
				>
					<Text font="--text-18-22-med" color="--text-dark-primary">No orders yet</Text>
					<Text font="--text-16-22-reg" :color="KOVA.textSecondary">
						Once your prescriber has issued your first prescription, your order will appear here.
					</Text>
				</LayoutFlex>
				<OrderCard v-for="order in model.inProgress" :key="order.id" :order="order" :query="route.query" />
				<OrdersNextShipmentTile :date="model.nextShipment" :warning="model.nextShipmentWarning" />
			</LayoutFlex>

			<LayoutFlex class="orders__group" padding="0" gap="var(--size-8)">
				<OrdersGroupHeader
					ref="allOrdersHeader"
					class="orders__group-header"
					title="All orders"
					:count="model.pastCount"
				/>
				<template v-if="pastPage && pastPage.items.length">
					<OrderCard v-for="order in pastPage.items" :key="order.id" :order="order" past :query="route.query" />
					<Pagination
						class="orders__pager"
						:page="pastPage.page"
						:page-count="pastPage.pageCount"
						aria-label="Order history pages"
						prev-label="Previous page"
						next-label="Next page"
						page-label="Page {page}"
						@ds:change="goToPage"
					/>
				</template>
				<Text v-else font="--text-16-22-med" :color="KOVA.textPrimary50">
					No past orders yet. Your history will appear here after your first delivery.
				</Text>
			</LayoutFlex>
		</LayoutFlex>
	</PortalPage>

	<!-- Teleported by the DS: a dialog with its focus trap, Escape, the overlay and the focus back on the card. Labelled
	     by the details' level-1 heading ("Order #N", or an unseen "Order details" until the order is there). The DS
	     forwards `data-order-drawer` to the dialog panel: the hook the focus watch below finds it by. -->
	<Drawer
		v-if="isWide"
		data-order-drawer
		:visible="orderId !== null"
		:aria-labelledby="DETAILS_HEADING_ID"
		@ds:close="closeDetails"
	>
		<OrderDetails
			v-if="orderId"
			:order-id="orderId"
			variant="drawer"
			:heading-id="DETAILS_HEADING_ID"
			@close="closeDetails"
		/>
	</Drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type ComponentPublicInstance, type Ref } from 'vue'
import { useRoute, useRouter, type LocationQuery, type RouteLocationNormalized } from 'vue-router'
import { Drawer, LayoutFlex, Pagination, Text } from '@simple-life-app/growth-ui-kova'
import OrderCard from '@/components/orders/OrderCard.vue'
import OrderDetails from '@/components/orders/OrderDetails.vue'
import OrdersGroupHeader from '@/components/orders/OrdersGroupHeader.vue'
import OrdersListSkeleton from '@/components/orders/OrdersListSkeleton.vue'
import OrdersNextShipmentTile from '@/components/orders/OrdersNextShipmentTile.vue'
import OrdersStateCard from '@/components/orders/OrdersStateCard.vue'
import RenewalActionBanner from '@/components/orders/RenewalActionBanner.vue'
import { KOVA } from '@/designTokens'
import { renewalCardDestination } from '@/orders/destinations'
import { buildOrdersListModel, paginate } from '@/orders/listModel'
import { renewalBannerModel } from '@/orders/renewalAction'
import PortalPage from '@/portal/components/PortalPage.vue'
import { portalSection } from '@/portal/sections'
import { keepScrollOn } from '@/router/keepScroll'
import { useOrdersStore } from '@/store/orders'

/**
 * The Orders section (KOVA-797): the "In progress" group (renewal card, "No orders yet", the in-progress orders,
 * the next-shipment tile) and "All orders" (the past orders a page at a time). It draws the models and makes no
 * decision of its own — `buildOrdersListModel` says what shows, `paginate` which page, `src/orders/destinations`
 * where a control leads (docs/architecture/orders.md).
 *
 * Data: `useOrdersStore` — every visit calls `loadList()` (a revisit shows the cached list while it re-reads), a
 * tab return `refreshOnVisible()`; a failed re-read — the list's or the banner feed's — keeps what is shown and
 * raises the stale notice (`store.stale`).
 *
 * One order (`/orders/:orderId`, the same view — vue-router keeps this instance across the two routes): from a
 * 1024px viewport the details open in the DS drawer over the list, below it they replace the list in the outlet.
 * Closing goes back to the list the order was opened from, `?page=` and all (docs/architecture/orders.md → Views).
 */

const section = portalSection('orders')
const store = useOrdersStore()
const route = useRoute()
const router = useRouter()

/** The details' level-1 heading, which the drawer is labelled by. A fixed id: never the order's number. */
const DETAILS_HEADING_ID = 'order-details-heading'

/** The open order, `null` on the list. Whatever the URL holds — the details say when it isn't an order. */
const orderId = computed(() => {
	const raw = route.params.orderId
	const value = Array.isArray(raw) ? raw[0] : raw
	return value ? value : null
})

// Drawer or page. The drawer is teleported out of the shell's `container: portal`, so the switch is the viewport's
// 1024px (as the DS Modal's), not the container query; with a classic scrollbar the shell's own switch comes ~15px
// later. Read at setup so the first render is already the right one, then followed — a crossing with an order open
// (a rotation, a resize) is handled with the focus below.
const wideQuery = window.matchMedia('(min-width: 1024px)')
const isWide = ref(wideQuery.matches)
function onWideChange(event: MediaQueryListEvent) {
	isWide.value = event.matches
}

const model = computed(() => (store.overview ? buildOrdersListModel(store.overview, store.banners) : null))

/**
 * The renewal card: the list model's — and, while a failed first load leaves no list, the feed's own (the same
 * `renewalBannerModel`), so an outstanding action never goes with the list.
 */
const renewal = computed(() => (model.value ? model.value.renewal : renewalBannerModel(store.banners ?? [])))

/** The renewal card's button — `null` (a text-only card) when its destination doesn't exist yet. */
const renewalDestination = computed(() => renewalCardDestination(store.banners, router))

// F6: the card's CTA leaves the section (the paywall is a route of its own), so the store remembers the action;
// the return here reads once more a few seconds later if billing hasn't cleared it yet. Never optimistic: the
// card stays until a read drops it (F7).
function openRenewal() {
	const banner = renewal.value
	const open = renewalDestination.value
	if (!banner || !open) return
	store.rememberOpenedAction(banner.id)
	open()
}

function followUpAfterReturn() {
	const actionId = store.takeOpenedAction()
	if (actionId) store.scheduleFollowUpRefresh(actionId)
}

type StateCard = { focusAction: () => void }
type GroupHeader = { focus: () => void; reveal: () => void }

const errorCard = ref<StateCard | null>(null)
const staleNotice = ref<StateCard | null>(null)
const inProgressHeader = ref<GroupHeader | null>(null)
const allOrdersHeader = ref<GroupHeader | null>(null)
const firstGroup = ref<ComponentPublicInstance | null>(null)

/** The stale notice's Try again, while its re-read is in flight — busy, as the details' own (OrderDetails). */
const retryingRefresh = ref(false)

/**
 * After a list retry, the focus its button had. A browser takes it off a button that turns disabled (the stale
 * notice's, busy) or goes (the error card's, swapped for the skeleton). When the card is back — the read failed
 * again — its new button takes it, so Enter retries once more; when the list took the card's place, the start of the
 * list, where reading resumes — the "In progress" heading, or the group itself when it has none (a renewal card or
 * "No orders yet" above "All orders"), as the details' retries focus their heading. Never scrolling: a user who
 * scrolled while it read stays there. A focus the user moved meanwhile stays where it is.
 */
async function refocusAfterRetry(card: Ref<StateCard | null>) {
	await nextTick()
	const active = document.activeElement
	if (active && active !== document.body) return
	if (card.value) card.value.focusAction()
	else if (inProgressHeader.value) inProgressHeader.value.focus()
	else (firstGroup.value?.$el as HTMLElement | undefined)?.focus({ preventScroll: true })
}

/**
 * The first-load error card's Try again: the skeleton in its place, then the list — or the card again. The focus
 * comes back when the list's read settles (`listState` leaves `loading`): not when the feed's does, which may still
 * be on its way, and also when a newer read overtook this one.
 */
let refocusAfterLoad = false
function retryLoad() {
	refocusAfterLoad = true
	void store.loadList()
}
watch(
	() => store.listState,
	(state) => {
		if (!refocusAfterLoad || state === 'loading') return
		refocusAfterLoad = false
		void refocusAfterRetry(errorCard)
	},
)

/**
 * The stale notice's Try again: the list and the feed again, the list staying on screen. One at a time — a second
 * tap while it reads sends nothing.
 */
async function retryRefresh() {
	if (retryingRefresh.value) return
	retryingRefresh.value = true
	await store.refreshAll()
	retryingRefresh.value = false
	await refocusAfterRetry(staleNotice)
}

// "All orders" a page at a time; `?page=` holds the page, so Back from an order returns to it. Anything the URL
// says that isn't a page of today's list lands on the nearest one (`paginate`), and the URL follows.
const requestedPage = computed(() => {
	const raw = route.query.page
	const value = Array.isArray(raw) ? raw[0] : raw
	return value === null || value === undefined ? 1 : Number(value)
})

const pastPage = computed(() => (model.value ? paginate(model.value.past, requestedPage.value) : null))

/** `query` with `page` set to `page` — page 1 is the bare URL — and every other parameter (the dev `?orders=`) kept. */
function queryWithPage(query: LocationQuery, page: number): LocationQuery {
	const { page: _current, ...rest } = query
	return page > 1 ? { ...rest, page: String(page) } : rest
}

// Only on the list's own URL: an order's URL carries the list's query as it was, for the way back.
watch(
	[() => pastPage.value?.page, () => route.query.page, orderId],
	([page, , openOrder]) => {
		if (page === undefined || openOrder) return
		const query = queryWithPage(route.query, page)
		if (query.page !== route.query.page) void router.replace({ path: route.path, query })
	},
	{ immediate: true },
)

async function goToPage(page: number) {
	await router.replace({ path: route.path, query: queryWithPage(route.query, page) })
	// Bring the new page in from its heading, with the focus there — after the router's `scrollBehavior` has
	// sent the window to the top (it does on every navigation, a query change too; its scroll settles in
	// microtasks, so one macrotask later the new page is drawn and the scroll is ours).
	await new Promise((resolve) => setTimeout(resolve))
	allOrdersHeader.value?.reveal()
}

// F3: a return to the tab re-reads (the store throttles it to once per 30 s) — and, if a CTA was opened in a new
// tab, counts as the return from it.
function onVisibilityChange() {
	if (document.visibilityState !== 'visible') return
	void store.refreshOnVisible(orderId.value ?? undefined)
	followUpAfterReturn()
}

/**
 * Back to the list: through the history when the entry before is that list (Back then lands on the page it was
 * on), else a new entry for it with this URL's query (`?page=`, the dev params). The entry before is what
 * vue-router keeps in `history.state.back` — `null` on the first entry of the app — not `history.length`: after
 * a link from an email or the sign-in redirect, the entry before is another site, or Auth0. One close at a time —
 * a second Escape before the route changes would go back twice.
 */
let closing = false
function closeDetails() {
	if (closing || !orderId.value) return
	closing = true
	const back: unknown = window.history.state?.back
	if (typeof back === 'string' && router.resolve(back).path === '/orders') router.back()
	else void router.push({ path: '/orders', query: route.query })
}

// The drawer opening over the list, or closing, is no new page: the list stays where it was. Only this view can tell a
// drawer from a page (it depends on the viewport), so it marks those navigations and the router's `scrollBehavior`
// leaves the window alone (`keepScroll.ts`); the shell never scrolls on a route change itself. Below 1024px an order is
// a page: it opens from the top, and Back to the list gets the router's saved offset.
const isOrdersRoute = (location: RouteLocationNormalized) => location.meta.portalSection === 'orders'
const stopKeepingScroll = router.beforeEach((to, from) => {
	if (isWide.value && isOrdersRoute(to) && isOrdersRoute(from) && to.path !== from.path) keepScrollOn(to)
})

watch(
	() => route.path,
	() => {
		closing = false
	},
)

const focusHeading = () => document.getElementById(DETAILS_HEADING_ID)?.focus({ preventScroll: true })

/** `id`'s card on the list as drawn now, if this page of it holds one. */
function cardOf(id: string): HTMLAnchorElement | undefined {
	const path = `/orders/${encodeURIComponent(id)}`
	return Array.from(document.querySelectorAll<HTMLAnchorElement>('a.order-card')).find((card) => card.pathname === path)
}

/**
 * Whether the drawer that just closed left the focus with nothing to go to: the DS gives it back to the control the
 * drawer opened from, and with none (a direct URL, a crossing from the page) leaves it where it was — inside the
 * panel, which stays in the DOM through its leave transition (0.25 s; a frame or two with reduced motion) and takes
 * the focus with it when it goes. Or it is already on the page body.
 */
function focusLeftWithClosedDrawer(): boolean {
	const active = document.activeElement
	return !active || active === document.body || active.closest('[data-order-drawer]') !== null
}

// The page variant moves the focus as a page would: to the order's heading when it opens from the list, and back to
// that order's card when the list returns. The drawer does both itself (its own focus, then the card again) — unless
// it opened with no control to give the focus back to (a direct URL, a crossing from the page): then the card takes
// it here as the drawer closes — at once, as the DS gives it back, not after the panel's leave — and the browser
// brings it into view if the list's offset left it out.
//
// A crossing of 1024px with an order open runs no navigation, so neither the router's `scrollBehavior` nor the
// watch on the order would move anything. Into the page, the order opens as a page does: from the top, its heading
// focused (the list's offset stays saved for Back). Into the drawer, the drawer takes the focus; the list shows under
// it where the page was scrolled to, and closing keeps that — the drawer's rule (decided: no jump back to where the
// list was before the order opened, docs/architecture/orders.md → Views).
watch([orderId, isWide], async ([id, wide], [previous, wasWide]) => {
	await nextTick()
	if (wide !== wasWide) {
		if (id && !wide) {
			window.scrollTo({ top: 0, left: 0 })
			focusHeading()
		}
		return
	}
	if (id && !previous && !wide) focusHeading()
	else if (!id && previous) {
		if (!wide) cardOf(previous)?.focus({ preventScroll: true })
		else if (focusLeftWithClosedDrawer()) cardOf(previous)?.focus()
	}
})

onMounted(() => {
	void store.loadList()
	followUpAfterReturn()
	document.addEventListener('visibilitychange', onVisibilityChange)
	wideQuery.addEventListener('change', onWideChange)
})

onBeforeUnmount(() => {
	document.removeEventListener('visibilitychange', onVisibilityChange)
	wideQuery.removeEventListener('change', onWideChange)
	stopKeepingScroll()
	store.cancelFollowUpRefresh()
})
</script>

<style scoped>
/* The list's own width decides how a card lays out (OrderCard: row from 560px) — the content column is only
   ~510px at a 1024px window, so the portal's switch would be the wrong one. */
.orders {
	--orders-gap: var(--size-28);

	container: orders / inline-size;
}

/* Figma's group titles are 8 under the row above them (the page header's 32 + 8; the tile's 24 + 16 on desktop,
   20 + 16 on mobile), 20 over their first card; cards 8 apart. */
.orders__group-header {
	margin-bottom: var(--size-12);
	padding-top: var(--size-8);
}

/* A focus target after a list retry only (tabindex -1, when "In progress" has no header), not a control: no ring. */
.orders__group:focus {
	outline: none;
}

/* "No orders yet" (Figma 13868:153025 / 14254:169804): the card's text 24 in on the collapsed cards' width, 32 in
   once the cards are rows — the same switch as OrderCard. */
.orders__empty {
	--orders-empty-inline: var(--size-24);
}

@container orders (min-width: 560px) {
	.orders__empty {
		--orders-empty-inline: var(--size-32);
	}
}

/* Figma: the pager 24 under the last card. */
.orders__pager {
	margin-top: var(--size-16);
}

@container portal (min-width: 1024px) {
	.orders {
		--orders-gap: var(--size-32);
	}
}
</style>
